/**
 * Loans Redux Slice
 * Manages loans state
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put } from '../../services/api/axiosInstance';
import { API_ENDPOINTS } from '../../services/api/apiEndpoints';

export const fetchLoans = createAsyncThunk(
  'loans/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.LOANS.BASE, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch loans');
    }
  }
);

export const fetchLoanById = createAsyncThunk(
  'loans/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.LOANS.BY_ID(id));
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan');
    }
  }
);

export const createLoan = createAsyncThunk(
  'loans/create',
  async (loanData, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.LOANS.BASE, loanData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create loan');
    }
  }
);

export const approveLoan = createAsyncThunk(
  'loans/approve',
  async (id, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.LOANS.APPROVE(id));
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve loan');
    }
  }
);

const initialState = {
  loans: [],
  selectedLoan: null,
  isLoading: false,
  error: null,
};

const loanSlice = createSlice({
  name: 'loans',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedLoan: (state) => {
      state.selectedLoan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoans.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLoans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loans = action.payload.data || action.payload;
      })
      .addCase(fetchLoans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLoanById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLoanById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedLoan = action.payload;
      })
      .addCase(fetchLoanById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createLoan.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createLoan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loans.unshift(action.payload);
      })
      .addCase(createLoan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSelectedLoan } = loanSlice.actions;
export default loanSlice.reducer;
